/**
 * Mileage rate math — SQL is the truth (migration 0022: compute_fuel_bracket,
 * submit_mileage_rate_request), this module is the client-side PREVIEW mirror
 * only (packages/logic/CLAUDE.md). Keep the bracket boundaries in sync with
 * the SQL function by hand — there is no code sharing between Deno/TS and
 * plpgsql. Vault: DATAMODEL § Mileage · Business Logic §8.
 */

/**
 * Fuel-price → bracket mapping. Brackets match the `bracket_label` values
 * seeded in `mileage_rate_config` (vault DATAMODEL.md):
 * '79below' | '80-89' | '90-99' | '100-109' | '110+'.
 */
export function computeFuelBracket(fuelPricePerLiter: number): string {
  if (fuelPricePerLiter <= 79) return "79below";
  if (fuelPricePerLiter <= 89) return "80-89";
  if (fuelPricePerLiter <= 99) return "90-99";
  if (fuelPricePerLiter <= 109) return "100-109";
  return "110+";
}

/** Fast-track threshold per PRD/Phase 4: fuel spike ≥ ₱10/L in one week. */
export function isFastTrackSpike(currentPrice: number, lastWeekAvg: number | null): boolean {
  if (lastWeekAvg === null) return false;
  return currentPrice - lastWeekAvg >= 10;
}

/** Mondays-only submission window, per Phase Tracker's "Monday-only UI enforcement + API check". */
export function isMileageSubmissionWindow(date: Date, isFastTrack: boolean): boolean {
  if (isFastTrack) return true; // fast-track bypasses the Monday-only rule
  return date.getDay() === 1; // Monday
}

/** One closed trip, as the rider's own `trips` rows carry it. */
export interface TripMileageInput {
  /** Server-computed by PostGIS over `trips.track` — never client-measured. */
  totalKm: number | null;
  /** Locked at trip creation and never recomputed — money-math.md §3. */
  mileageRateSnapshot: number | null;
}

export interface TripMileageLine {
  km: number;
  ratePerKm: number;
  amountPhp: number;
}

export interface TripMileagePreview {
  /** Σ (km × that trip's own snapshot). NOT take-home pay — mileage only. */
  amountPhp: number;
  totalKm: number;
  /** Trips excluded because they are still open or were never rated. */
  skippedCount: number;
  lines: TripMileageLine[];
}

/**
 * Rider mileage PREVIEW from the rider's own closed trips.
 *
 * Every trip is multiplied by ITS OWN `mileage_rate_snapshot` — money-math.md §3:
 * a rate approved today must never reach a trip that closed under the old one.
 * There is deliberately no single-rate parameter: a signature that accepted one
 * would make re-reading the live `mileage_rate_config` the easy mistake, which
 * is precisely the mistake the snapshot exists to prevent.
 *
 * A trip missing either value is SKIPPED and counted, never defaulted to zero:
 * a zero silently understates pay, whereas a skip is a number the caller can
 * show ("2 trips not yet rated"). `total_km` is null until the server closes the
 * trip, so an open trip landing here is normal, not an error.
 *
 * This is a preview, not payroll. Payroll is the nightly SQL run (0019) and it
 * is the only truth; nothing here is payable.
 */
export function sumTripMileagePreview(trips: readonly TripMileageInput[]): TripMileagePreview {
  const lines: TripMileageLine[] = [];
  let skippedCount = 0;

  for (const trip of trips) {
    if (trip.totalKm === null || trip.mileageRateSnapshot === null) {
      skippedCount += 1;
      continue;
    }
    lines.push({
      km: trip.totalKm,
      ratePerKm: trip.mileageRateSnapshot,
      amountPhp: round2(trip.totalKm * trip.mileageRateSnapshot),
    });
  }

  return {
    // Round each line, then the sum: the per-line figures are what the rider
    // reads, and a total that does not equal the lines they can see reads as a
    // bug even when it is the more precise number.
    amountPhp: round2(lines.reduce((total, line) => total + line.amountPhp, 0)),
    totalKm: round2(lines.reduce((total, line) => total + line.km, 0)),
    skippedCount,
    lines,
  };
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}
